import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Divider,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Snackbar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CloudIcon from '@material-ui/icons/Cloud';
import PowerIcon from '@material-ui/icons/PowerSettingsNew';
import BuildIcon from '@material-ui/icons/Build';
import PublishIcon from '@material-ui/icons/Publish';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassIcon from '@material-ui/icons/HourglassEmpty';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import WarningIcon from '@material-ui/icons/Warning';
import SystemUpdateIcon from '@material-ui/icons/SystemUpdate';
import utils from '../../../universal/utils';
import useIsMountedRef from '../../../hooks/useIsMountedRef';

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(2) },
  summaryCard: { marginBottom: theme.spacing(3) },
  regionCard: { marginBottom: theme.spacing(2) },
  regionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  regionHeaderMain: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.25),
  },
  regionName: { fontWeight: 700, fontSize: '1.1rem' },
  regionSubtitle: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
  },
  chip: { marginRight: theme.spacing(0.5) },
  ltId: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    backgroundColor: theme.palette.action.hover,
    padding: '2px 6px',
    borderRadius: 4,
    display: 'inline-block',
    maxWidth: '100%',
    overflowX: 'auto',
  },
  capacityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  capacityInput: { width: 80 },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
  },
  instanceTable: { marginTop: theme.spacing(1) },
  progressText: { fontSize: '0.78rem', color: theme.palette.text.secondary },
  copyBtn: { padding: 2, marginLeft: 4 },
  diagnosticCard: { marginTop: theme.spacing(1.5) },
  diagnosticTableWrap: { overflowX: 'auto' },
  diagnosticNote: { display: 'block', marginTop: theme.spacing(0.5) },
}));

const apiUrl = (path) => `${utils.getEdgeBasePath()}${path}`;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const WARM_SCHEDULE_SUGGESTIONS = {
  eume: {
    label: 'Europe & Middle East',
    ianaTimezone: 'Europe/Paris',
    openHourUTC: 10,
    closeHourUTC: 23,
    regions: [
      'eu-central-1',
      'eu-central-2',
      'eu-west-1',
      'eu-west-2',
      'eu-west-3',
      'eu-south-1',
      'eu-south-2',
      'eu-north-1',
      'il-central-1',
    ],
  },
  na: {
    label: 'North America',
    ianaTimezone: 'America/New_York',
    openHourUTC: 14,
    closeHourUTC: 5,
    regions: ['us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'ca-central-1', 'ca-west-1'],
  },
  sa: {
    label: 'South America',
    ianaTimezone: 'America/Sao_Paulo',
    openHourUTC: 14,
    closeHourUTC: 4,
    regions: ['sa-east-1'],
  },
  apac: {
    label: 'Asia-Pacific',
    ianaTimezone: 'Asia/Tokyo',
    openHourUTC: 9,
    closeHourUTC: 17,
    regions: [
      'ap-northeast-1',
      'ap-northeast-2',
      'ap-northeast-3',
      'ap-southeast-1',
      'ap-southeast-2',
      'ap-southeast-4',
      'ap-south-1',
      'ap-south-2',
      'ap-east-1',
    ],
  },
};

function buildDefaultAmiName() {
  return `ouro-cs2-updated-${new Date().toISOString().slice(0, 10)}`;
}

function buildCleanupSummary(cleanup) {
  if (!cleanup) return '';
  const parts = [];
  if (cleanup.asg?.deleted) parts.push('ASG deleted');
  if (cleanup.launchTemplate?.deleted) parts.push('LT deleted');
  if (cleanup.amis?.deleted?.length) parts.push(`${cleanup.amis.deleted.length} AMI(s) removed`);
  if (cleanup.amis?.snapshots?.length) parts.push(`${cleanup.amis.snapshots.length} snapshot(s) removed`);
  if (cleanup.securityGroups?.deleted?.length) parts.push(`${cleanup.securityGroups.deleted.length} SG(s) removed`);
  if (cleanup.errors?.length) parts.push(`${cleanup.errors.length} warning(s)`);
  return parts.length ? parts.join(', ') + '.' : 'No AWS resources found to clean.';
}

function padHour(hour) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function formatUtcHourInZone(utcHour, ianaTimezone) {
  const d = new Date();
  d.setUTCHours(utcHour, 0, 0, 0);
  return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: ianaTimezone,
  });
}

function getWarmSuggestionForRegion(awsRegion) {
  return Object.values(WARM_SCHEDULE_SUGGESTIONS).find((suggestion) =>
    suggestion.regions.includes(awsRegion)
  ) || WARM_SCHEDULE_SUGGESTIONS.eume;
}

function buildWarmScheduleState(region = {}) {
  const suggestion = getWarmSuggestionForRegion(region.awsRegion);
  const warmSchedule = region.warmSchedule || {};
  return {
    enabled: warmSchedule.enabled === true,
    warmMinimum: warmSchedule.warmMinimum ?? 1,
    openHourUTC: warmSchedule.openHourUTC ?? suggestion.openHourUTC,
    closeHourUTC: warmSchedule.closeHourUTC ?? suggestion.closeHourUTC,
    idleTimeoutMinutes: warmSchedule.idleTimeoutMinutes ?? 15,
    reactivationMinPlayers: warmSchedule.reactivationMinPlayers ?? 5,
    reactivationMinMinutes: warmSchedule.reactivationMinMinutes ?? 1,
  };
}

// ─── Status badge helper ───────────────────────────────────────────────────────
function StatusChip({ status, label }) {
  const map = {
    ready: { color: 'primary', icon: <CheckCircleIcon style={{ fontSize: 14 }} /> },
    done: { color: 'primary', icon: <CheckCircleIcon style={{ fontSize: 14 }} /> },
    idle: { color: 'default', icon: null },
    cold: { color: 'default', icon: null },
    pending: { color: 'default', icon: <HourglassIcon style={{ fontSize: 14 }} /> },
    baking: { color: 'default', icon: <HourglassIcon style={{ fontSize: 14 }} /> },
    failed: { color: 'secondary', icon: <ErrorIcon style={{ fontSize: 14 }} /> },
  };
  const cfg = map[status] || map.idle;
  return (
    <Chip
      size="small"
      color={cfg.color}
      icon={cfg.icon}
      label={label || status}
      style={{ marginRight: 4 }}
    />
  );
}

function buildPresentationState(region = {}) {
  return {
    displayName: region.displayName || '',
    country: region.country || '',
    metaDescription: region.metaDescription || '',
    flagImageUrl: region.flagImageUrl || '',
  };
}

function buildCountryOptions(countryOptions = [], selectedCountry = '', selectedFlagImageUrl = '') {
  const normalizedCountry = selectedCountry?.trim();
  const options = Array.isArray(countryOptions) ? countryOptions : [];

  if (!normalizedCountry) return options;

  const alreadyIncluded = options.some((option) => option.displayName === normalizedCountry);
  if (alreadyIncluded) return options;

  return [
    {
      displayName: normalizedCountry,
      flagImageUrl: selectedFlagImageUrl || '',
    },
    ...options,
  ];
}

function RegionPresentationFields({
  value,
  onChange,
  disabled = false,
  requireDisplayName = false,
  countryOptions = [],
  loadingCountries = false,
}) {
  const flagPreviewUrl = value.flagImageUrl?.trim();
  const subtitlePreview = value.metaDescription?.trim() || value.country?.trim();
  const availableCountryOptions = buildCountryOptions(
    countryOptions,
    value.country,
    value.flagImageUrl
  );

  return (
    <Box mt={1}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Display Name"
            fullWidth
            required={requireDisplayName}
            variant="outlined"
            size="small"
            value={value.displayName}
            disabled={disabled}
            onChange={(e) => onChange('displayName', e.target.value)}
            placeholder="e.g. Frankfurt"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined" size="small" disabled={disabled || loadingCountries}>
            <InputLabel>Flag Country (optional)</InputLabel>
            <Select
              value={value.country || ''}
              label="Flag Country (optional)"
              onChange={(e) => {
                const selectedCountry = e.target.value;
                const selectedOption = availableCountryOptions.find(
                  (option) => option.displayName === selectedCountry
                );

                onChange('country', selectedCountry);
                onChange('flagImageUrl', selectedOption?.flagImageUrl || '');
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableCountryOptions.map((option) => (
                <MenuItem key={option.displayName} value={option.displayName}>
                  <Box display="flex" alignItems="center" gridGap={8}>
                    {option.flagImageUrl ? (
                      <img
                        src={option.flagImageUrl}
                        alt={`${option.displayName} flag`}
                        style={{
                          width: 22,
                          height: 16,
                          objectFit: 'cover',
                          borderRadius: 3,
                          border: '1px solid rgba(255, 255, 255, 0.12)',
                        }}
                      />
                    ) : (
                      <Box
                        width={22}
                        height={16}
                        borderRadius={3}
                        style={{ border: '1px solid rgba(255, 255, 255, 0.12)' }}
                      />
                    )}
                    <span>{option.displayName}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Secondary Description (optional)"
            fullWidth
            variant="outlined"
            size="small"
            value={value.metaDescription}
            disabled={disabled}
            onChange={(e) => onChange('metaDescription', e.target.value)}
            placeholder="Shown below the region name for players"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Flag Image URL (optional)"
            fullWidth
            variant="outlined"
            size="small"
            value={value.flagImageUrl}
            disabled={disabled}
            onChange={(e) => onChange('flagImageUrl', e.target.value)}
            placeholder="Optional manual override"
            helperText="Auto-filled from the selected country, but still editable."
          />
        </Grid>
      </Grid>

      {flagPreviewUrl && (
        <Box mt={1.5} display="flex" alignItems="center" gap={12}>
          <img
            src={flagPreviewUrl}
            alt={`${value.displayName || value.country || 'Region'} flag`}
            style={{
              width: 28,
              height: 20,
              objectFit: 'cover',
              borderRadius: 4,
              border: '1px solid rgba(255, 255, 255, 0.12)',
            }}
          />
          <Typography variant="body2" color="textSecondary">
            {subtitlePreview || 'Flag preview'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Add Region Dialog ─────────────────────────────────────────────────────────
function AddRegionDialog({ open, onClose, onSubmit, userId, countryOptions, loadingCountries }) {
  const isMountedRef = useIsMountedRef();
  const [awsRegions, setAwsRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [form, setForm] = useState({ awsRegion: '', cold: false, ...buildPresentationState() });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingRegions(true);
    fetch(apiUrl(`/api/v7/admin/aws-regions/adpass/${userId}`))
      .then((r) => r.json())
      .then((data) => {
        if (isMountedRef.current && data.ok) setAwsRegions(data.regions || []);
      })
      .catch(() => {})
      .finally(() => {
        if (isMountedRef.current) setLoadingRegions(false);
      });
  }, [isMountedRef, open, userId]);

  useEffect(() => {
    if (!open) {
      setForm({ awsRegion: '', cold: false, ...buildPresentationState() });
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!form.awsRegion || !form.displayName.trim()) return;
    setSubmitting(true);
    await onSubmit(form);
    if (!isMountedRef.current) return;
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Region</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={16} mt={1}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>AWS Region</InputLabel>
            <Select
              value={form.awsRegion}
              onChange={(e) => {
                const value = e.target.value;
                setForm((f) => ({ ...f, awsRegion: value }));
              }}
              label="AWS Region"
              disabled={loadingRegions}
            >
              {awsRegions.map((r) => (
                <MenuItem key={r.regionName} value={r.regionName}>
                  {r.regionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box mt={2}>
            <RegionPresentationFields
              value={form}
              requireDisplayName
              countryOptions={countryOptions}
              loadingCountries={loadingCountries}
              onChange={(field, value) => {
                setForm((current) => ({ ...current, [field]: value }));
              }}
            />
          </Box>
          <Box mt={2} p={1.5} bgcolor="action.hover" borderRadius={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.cold === true}
                  onChange={(e) => setForm((f) => ({ ...f, cold: e.target.checked }))}
                  color="primary"
                  size="small"
                />
              }
              label="Add as cold region (metadata only, no AWS infrastructure)"
            />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
              {form.cold
                ? 'Creates only a database record. The region will be visible to players and selectable as a preference, but no servers will run here until you activate it from the region card.'
                : 'Ouro will automatically copy the AMI, clone the security group into the target region\'s default VPC, create the launch template, and create the ASG for you.'}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              If the country matches Ouro's flag catalog, the flag can be auto-resolved on save. You can always override the flag image URL manually.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting || !form.awsRegion || !form.displayName.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : <CloudIcon />}
        >
          {submitting ? 'Starting…' : form.cold ? 'Add Cold Region' : 'Add & Provision Region'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Bake AMI Dialog ───────────────────────────────────────────────────────────
function BakeAmiDialog({ open, onClose, onSubmit, regionId, userId }) {
  const isMountedRef = useIsMountedRef();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [amiName, setAmiName] = useState(buildDefaultAmiName);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(null);
    setAmiName(buildDefaultAmiName());
    fetch(apiUrl(`/api/v7/admin/server-regions/${regionId}/instances/adpass/${userId}`))
      .then((r) => r.json())
      .then((data) => {
        if (isMountedRef.current && data.ok) setInstances(data.instances || []);
      })
      .catch(() => {})
      .finally(() => {
        if (isMountedRef.current) setLoading(false);
      });
  }, [isMountedRef, open, regionId, userId]);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    await onSubmit({ instanceId: selected, name: amiName || undefined });
    if (isMountedRef.current) setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bake New AMI</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Select a healthy, fully-updated instance to snapshot as the new AMI.
          The instance will remain running — no reboot is performed.
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={2}><CircularProgress /></Box>
        ) : instances.length === 0 ? (
          <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
            No healthy in-service instances found in this region's ASG.
          </Typography>
        ) : (
          <Table size="small" style={{ marginTop: 12 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Instance ID</TableCell>
                <TableCell>Public IP</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Launch Time</TableCell>
                <TableCell>Health</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {instances.map((inst) => (
                <TableRow
                  key={inst.instanceId}
                  hover
                  selected={selected === inst.instanceId}
                  onClick={() => setSelected(inst.instanceId)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <input
                      type="radio"
                      checked={selected === inst.instanceId}
                      onChange={() => setSelected(inst.instanceId)}
                    />
                  </TableCell>
                  <TableCell><code>{inst.instanceId}</code></TableCell>
                  <TableCell>{inst.publicIp || '—'}</TableCell>
                  <TableCell>{inst.instanceType || '—'}</TableCell>
                  <TableCell>
                    {inst.launchTime ? new Date(inst.launchTime).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color="primary" label={inst.healthStatus} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Box mt={2}>
          <TextField
            label="AMI Name (optional)"
            fullWidth
            variant="outlined"
            size="small"
            value={amiName}
            onChange={(e) => setAmiName(e.target.value)}
            placeholder={buildDefaultAmiName()}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting || !selected}
          startIcon={submitting ? <CircularProgress size={16} /> : <BuildIcon />}
        >
          {submitting ? 'Starting bake…' : 'Bake AMI'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PermissionStatusChip({ status }) {
  const map = {
    ok: { label: 'OK', color: 'primary' },
    issues: { label: 'Issues Found', color: 'secondary' },
    missing_permission: { label: 'Missing Permission', color: 'secondary' },
    resource_missing: { label: 'Resource Missing', color: 'default' },
    error: { label: 'Error', color: 'secondary' },
    skipped: { label: 'Skipped', color: 'default' },
  };
  const cfg = map[status] || { label: status || 'Unknown', color: 'default' };
  return <Chip size="small" color={cfg.color} label={cfg.label} />;
}

function PermissionChecksTable({ checks }) {
  const classes = useStyles();

  return (
    <Box className={classes.diagnosticTableWrap}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Check</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Required IAM Actions</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {checks.map((check) => (
            <TableRow key={check.key}>
              <TableCell>{check.label}</TableCell>
              <TableCell>
                <PermissionStatusChip status={check.status} />
              </TableCell>
              <TableCell>{(check.requiredPermissions || []).join(', ') || '—'}</TableCell>
              <TableCell>{check.message || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function PermissionsDiagnosticPanel({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runScan = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/permissions-scan/adpass/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || data.err);
      if (isMountedRef.current) setScan(data.scan || null);
    } catch (err) {
      if (isMountedRef.current) setError(err.message);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const summary = scan?.summary;

  return (
    <Paper variant="outlined" style={{ padding: 16, marginBottom: 20 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={12}>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            AWS Region Permission Scan
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Checks every AWS region enabled in the account and shows which IAM actions are still missing for Frankfurt AMI propagation, target-region security group cloning, launch template management, and ASG access.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={runScan}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          {loading ? 'Scanning…' : 'Scan All AWS Regions'}
        </Button>
      </Box>

      {error && (
        <Typography color="error" style={{ marginTop: 12 }}>
          Scan error: {error}
        </Typography>
      )}

      {scan && (
        <Box mt={2}>
          <Box display="flex" flexWrap="wrap" gap={8}>
            <Chip size="small" label={`${summary?.checkedRegions || 0} regions checked`} />
            <Chip size="small" color="primary" label={`${summary?.healthyRegions || 0} ready`} />
            <Chip size="small" color={summary?.issueRegions ? 'secondary' : 'default'} label={`${summary?.issueRegions || 0} with issues`} />
            <Chip
              size="small"
              variant="outlined"
              label={`Scanned ${new Date(scan.scannedAt).toLocaleString()}`}
            />
          </Box>

          <Typography variant="caption" color="textSecondary" className={classes.diagnosticNote}>
            Source region: Frankfurt ({scan.sourceRegion}) using AMI {scan.sourceAmiId} and launch template {scan.sourceLaunchTemplateId}.
          </Typography>
          {(scan.notes || []).map((note) => (
            <Typography key={note} variant="caption" color="textSecondary" className={classes.diagnosticNote}>
              {note}
            </Typography>
          ))}

          {!!summary?.missingPermissions?.length && (
            <Box mt={2}>
              <Typography variant="subtitle2">Missing IAM Actions</Typography>
              <Box display="flex" flexWrap="wrap" gap={8} mt={1}>
                {summary.missingPermissions.map((permission) => (
                  <Chip
                    key={permission}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    label={permission}
                  />
                ))}
              </Box>
            </Box>
          )}

          {!!scan.globalChecks?.length && (
            <Card className={classes.diagnosticCard} variant="outlined">
              <CardContent>
                <Typography variant="subtitle2">Account-Level Checks</Typography>
                <PermissionChecksTable checks={scan.globalChecks} />
              </CardContent>
            </Card>
          )}

          {!!scan.sourceChecks?.length && (
            <Card className={classes.diagnosticCard} variant="outlined">
              <CardContent>
                <Typography variant="subtitle2">Frankfurt Source Checks</Typography>
                <PermissionChecksTable checks={scan.sourceChecks} />
              </CardContent>
            </Card>
          )}

          <Box mt={2}>
            <Typography variant="subtitle2">Per-Region Results</Typography>
            {(scan.regions || []).map((region) => (
              <Card key={region.awsRegion} className={classes.diagnosticCard} variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={8}>
                    <Box display="flex" alignItems="center" gap={8} flexWrap="wrap">
                      <Typography style={{ fontWeight: 600 }}>{region.awsRegion}</Typography>
                      {region.isSourceRegion && (
                        <Chip size="small" color="primary" label="Source" />
                      )}
                      <PermissionStatusChip status={region.status} />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {region.endpoint || 'AWS regional endpoint'}
                    </Typography>
                  </Box>

                  {!!region.missingPermissions?.length && (
                    <Box display="flex" flexWrap="wrap" gap={8} mt={1}>
                      {region.missingPermissions.map((permission) => (
                        <Chip
                          key={`${region.awsRegion}-${permission}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          label={permission}
                        />
                      ))}
                    </Box>
                  )}

                  {!region.missingPermissions?.length && (
                    <Typography variant="caption" color="textSecondary" className={classes.diagnosticNote}>
                      All checked actions were available in this region.
                    </Typography>
                  )}

                  <Box mt={2}>
                    <PermissionChecksTable checks={region.checks || []} />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

// ─── Region Card ──────────────────────────────────────────────────────────────
function RegionCard({ region, userId, onRefresh, countryOptions, loadingCountries, hasBakeInProgress }) {
  const classes = useStyles();
  const [capacityEdit, setCapacityEdit] = useState({
    min: region.minCapacity ?? 0,
    max: region.maxCapacity ?? 10,
    desired: region.desiredCapacity ?? 0,
  });
  const [warmScheduleEdit, setWarmScheduleEdit] = useState(buildWarmScheduleState(region));
  const [presentationEdit, setPresentationEdit] = useState(buildPresentationState(region));
  const [savingPresentation, setSavingPresentation] = useState(false);
  const [savingCapacity, setSavingCapacity] = useState(false);
  const [savingWarmSchedule, setSavingWarmSchedule] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [bakeDialogOpen, setBakeDialogOpen] = useState(false);
  const [retryingProvision, setRetryingProvision] = useState(false);
  const [snack, setSnack] = useState(null);

  useEffect(() => {
    setCapacityEdit({
      min: region.minCapacity ?? 0,
      max: region.maxCapacity ?? 10,
      desired: region.desiredCapacity ?? 0,
    });
    setWarmScheduleEdit(buildWarmScheduleState(region));
    setPresentationEdit(buildPresentationState(region));
  }, [
    region.displayName,
    region.country,
    region.metaDescription,
    region.flagImageUrl,
    region.minCapacity,
    region.maxCapacity,
    region.desiredCapacity,
    region.warmSchedule,
  ]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => setSnack('Copied!'));
  };

  const handleToggleEnabled = async () => {
    if (region.isSourceRegion) return;
    setTogglingEnabled(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/adpass/${userId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !region.enabled }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || data.err);
      onRefresh();
    } catch (err) {
      setSnack(`Error: ${err.message}`);
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleSaveCapacity = async () => {
    setSavingCapacity(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/adpass/${userId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          minCapacity: Number(capacityEdit.min),
          maxCapacity: Number(capacityEdit.max),
          desiredCapacity: Number(capacityEdit.desired),
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.asgWarning || data.error || data.err);
      if (data.asgWarning) setSnack(`Saved, but ASG warning: ${data.asgWarning}`);
      else setSnack('Capacity updated');
      onRefresh();
    } catch (err) {
      setSnack(`Error: ${err.message}`);
    } finally {
      setSavingCapacity(false);
    }
  };

  const handleUseSuggestedWarmSchedule = () => {
    const suggestion = getWarmSuggestionForRegion(region.awsRegion);
    setWarmScheduleEdit((current) => ({
      ...current,
      enabled: true,
      warmMinimum: current.warmMinimum || 1,
      openHourUTC: suggestion.openHourUTC,
      closeHourUTC: suggestion.closeHourUTC,
    }));
  };

  const handleSaveWarmSchedule = async () => {
    setSavingWarmSchedule(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/adpass/${userId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          warmSchedule: {
            enabled: warmScheduleEdit.enabled === true,
            warmMinimum: Math.max(1, Number(warmScheduleEdit.warmMinimum) || 1),
            openHourUTC: Number(warmScheduleEdit.openHourUTC),
            closeHourUTC: Number(warmScheduleEdit.closeHourUTC),
            idleTimeoutMinutes: Math.max(5, Math.min(60, Number(warmScheduleEdit.idleTimeoutMinutes) || 15)),
            reactivationMinPlayers: Math.max(1, Math.min(20, Number(warmScheduleEdit.reactivationMinPlayers) || 5)),
            reactivationMinMinutes: Math.max(1, Math.min(10, Number(warmScheduleEdit.reactivationMinMinutes) || 1)),
          },
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || data.err);
      setSnack('Warm schedule updated');
      onRefresh();
    } catch (err) {
      setSnack(`Error: ${err.message}`);
    } finally {
      setSavingWarmSchedule(false);
    }
  };

  const handleSavePresentation = async () => {
    if (!presentationEdit.displayName.trim()) {
      setSnack('Display name is required');
      return;
    }

    setSavingPresentation(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/adpass/${userId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          displayName: presentationEdit.displayName,
          country: presentationEdit.country,
          metaDescription: presentationEdit.metaDescription,
          flagImageUrl: presentationEdit.flagImageUrl,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || data.err);
      setSnack('Player-facing region details updated');
      onRefresh();
    } catch (err) {
      setSnack(`Error: ${err.message}`);
    } finally {
      setSavingPresentation(false);
    }
  };

  const handleShutdown = async () => {
    setSavingCapacity(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/adpass/${userId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minCapacity: 0, desiredCapacity: 0 }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.asgWarning || data.error || data.err);
      setSnack('Shutdown to 0');
      setCapacityEdit((c) => ({ ...c, min: 0, desired: 0 }));
      onRefresh();
    } catch (err) {
      setSnack(`Error: ${err.message}`);
    } finally {
      setSavingCapacity(false);
    }
  };

  const handleBake = async ({ instanceId, name }) => {
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/bake-ami/adpass/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instanceId, name }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || data.err);
      setSnack('AMI bake started — this takes 10–30 minutes.');
      setBakeDialogOpen(false);
      onRefresh();
    } catch (err) {
      setSnack(`Bake error: ${err.message}`);
    }
  };

  const handlePropagate = async () => {
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/propagate-ami/adpass/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || data.err);
      setSnack(data.message || 'Propagation started');
      onRefresh();
    } catch (err) {
      setSnack(`Propagation error: ${err.message}`);
    }
  };

  const handleRetryProvision = async () => {
    setRetryingProvision(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/retry-provision/adpass/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || data.err);
      setSnack('Provisioning retried — refresh in a moment to check progress.');
      onRefresh();
    } catch (err) {
      setSnack(`Retry error: ${err.message}`);
    } finally {
      setRetryingProvision(false);
    }
  };

  const [activating, setActivating] = useState(false);

  const handleActivate = async () => {
    if (!window.confirm(
      `Activate "${region.displayName}"?\n\n` +
      `This will copy the AMI to ${region.awsRegion}, create the launch template, and create the Auto Scaling group. ` +
      `The region will be available for match placement once provisioning completes.`
    )) return;
    setActivating(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/activate/adpass/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || data.err);
      setSnack('Infrastructure provisioning started — AMI copy, launch template, and ASG will be created in the background.');
      onRefresh();
    } catch (err) {
      setSnack(`Activation error: ${err.message}`);
    } finally {
      setActivating(false);
    }
  };

  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(
      `Remove "${region.displayName}" from Ouro?\n\n` +
      `This will delete the AWS infrastructure for ${region.awsRegion}: ` +
      `ASG, launch template, AMIs, snapshots, and security groups.\n\n` +
      `This cannot be undone.`
    )) return;
    setDeleting(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/${region._id}/adpass/${userId}`), {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!data.ok) {
        const summary = buildCleanupSummary(data.cleanup);
        throw new Error(data.error || data.err || `Cleanup errors: ${summary}`);
      }
      const summary = buildCleanupSummary(data.cleanup);
      setSnack(`Removed. ${summary}`);
      onRefresh();
    } catch (err) {
      setSnack(`Delete error: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const asg = region.liveAsg;
  const isCold = Boolean(region.isCold);
  const isBaking = region.amiBakeStatus === 'baking';
  const isPending =
    region.amiCopyStatus === 'pending' ||
    region.ltCopyStatus === 'pending' ||
    region.asgProvisionStatus === 'pending';
  const hasFailed =
    region.amiCopyStatus === 'failed' ||
    region.ltCopyStatus === 'failed' ||
    region.asgProvisionStatus === 'failed';
  const warmSuggestion = getWarmSuggestionForRegion(region.awsRegion);
  const warmWindowLabel = `${padHour(Number(warmScheduleEdit.openHourUTC))}-${padHour(Number(warmScheduleEdit.closeHourUTC))} UTC`;
  const warmLocalLabel = `${formatUtcHourInZone(Number(warmScheduleEdit.openHourUTC), warmSuggestion.ianaTimezone)}-${formatUtcHourInZone(Number(warmScheduleEdit.closeHourUTC), warmSuggestion.ianaTimezone)} ${warmSuggestion.label}`;
  const asgMinFloor = Number(region.minCapacity) || 0;
  const showAsgMinFloorHint = warmScheduleEdit.enabled !== true && asgMinFloor > 0;

  return (
    <Card className={classes.regionCard} variant="outlined">
      <CardContent>
        {/* Header row */}
        <Box className={classes.regionHeader}>
          <Box className={classes.regionHeaderMain}>
            {region.flagImageUrl && (
              <img
                src={region.flagImageUrl}
                alt={`${region.displayName} flag`}
                style={{
                  width: 28,
                  height: 20,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                }}
              />
            )}
            <Box>
              <Typography className={classes.regionName}>{region.displayName}</Typography>
              {(region.metaDescription || region.country) && (
                <Typography className={classes.regionSubtitle}>
                  {region.metaDescription || region.country}
                </Typography>
              )}
            </Box>
            <Chip size="small" label={region.awsRegion} variant="outlined" style={{ marginLeft: 8 }} />
            {region.isSourceRegion && (
              <Chip size="small" label="Source" color="primary" style={{ marginLeft: 4 }} />
            )}
            {region.isCold && (
              <Chip size="small" label="Cold" variant="outlined" style={{ marginLeft: 4 }} />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!region.enabled}
                  onChange={handleToggleEnabled}
                  disabled={togglingEnabled || region.isSourceRegion}
                  color="primary"
                  size="small"
                />
              }
              label={region.isSourceRegion ? 'Enabled (required)' : region.enabled ? 'Enabled' : 'Disabled'}
            />
            {!region.isSourceRegion && (
              <Tooltip title={isPending || isBaking ? 'Wait for provisioning or AMI bake to finish before deleting' : 'Remove region and delete AWS infra (ASG, LT, AMIs, SGs)'}>
                <span>
                  <IconButton size="small" onClick={handleDelete} color="secondary" disabled={deleting || isPending || isBaking}>
                    {deleting ? <CircularProgress size={16} color="secondary" /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Divider style={{ margin: '12px 0' }} />

        <Box mb={2}>
          <Typography variant="caption" color="textSecondary">
            Player-facing region details
          </Typography>
          <RegionPresentationFields
            value={presentationEdit}
            requireDisplayName
            disabled={savingPresentation}
            countryOptions={countryOptions}
            loadingCountries={loadingCountries}
            onChange={(field, value) => {
              setPresentationEdit((current) => ({ ...current, [field]: value }));
            }}
          />
          <Box mt={1.5}>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={handleSavePresentation}
              disabled={savingPresentation || !presentationEdit.displayName.trim()}
              startIcon={savingPresentation ? <CircularProgress size={14} /> : null}
            >
              Save Region Details
            </Button>
          </Box>
        </Box>

        {isCold ? (
          /* ── Cold region: no AWS infra yet ── */
          <Box mt={1} p={1.5} bgcolor="action.hover" borderRadius={4}>
            <Typography variant="body2" gutterBottom>
              This region is visible to players and selectable as a server preference, but no infrastructure has been provisioned yet. Matches will not be placed here — a running region will serve them instead.
            </Typography>
            <Box mt={1}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleActivate}
                disabled={activating}
                startIcon={activating ? <CircularProgress size={14} /> : <CloudIcon fontSize="small" />}
              >
                {activating ? 'Activating…' : 'Activate Infrastructure'}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {/* Status badges */}
            <Box display="flex" flexWrap="wrap" gap={4} mb={1} alignItems="center">
              <StatusChip status={region.amiCopyStatus || 'ready'} label={`AMI: ${region.amiCopyStatus || 'ready'}`} />
              <StatusChip status={region.ltCopyStatus || 'ready'} label={`LT: ${region.ltCopyStatus || 'ready'}`} />
              {region.asgProvisionStatus && (
                <StatusChip status={region.asgProvisionStatus} label={`ASG: ${region.asgProvisionStatus}`} />
              )}
              {region.amiBakeStatus && region.amiBakeStatus !== 'idle' && (
                <StatusChip status={region.amiBakeStatus} label={`Bake: ${region.amiBakeStatus}`} />
              )}
              {hasFailed && !region.isSourceRegion && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  disabled={retryingProvision}
                  onClick={handleRetryProvision}
                  startIcon={retryingProvision ? <CircularProgress size={14} /> : null}
                >
                  {retryingProvision ? 'Retrying…' : 'Retry Provision'}
                </Button>
              )}
            </Box>

            {/* AMI & LT IDs */}
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">AMI ID</Typography>
                <Box display="flex" alignItems="center">
                  <span className={classes.ltId}>{region.amiId || '—'}</span>
                  {region.amiId && (
                    <Tooltip title="Copy AMI ID">
                      <IconButton className={classes.copyBtn} size="small" onClick={() => copyToClipboard(region.amiId)}>
                        <FileCopyIcon style={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Launch Template ID</Typography>
                <Box display="flex" alignItems="center">
                  <span className={classes.ltId}>{region.launchTemplateId || '—'}</span>
                  {region.launchTemplateId && (
                    <Tooltip title="Copy LT ID (use in AWS console to create ASG)">
                      <IconButton className={classes.copyBtn} size="small" onClick={() => copyToClipboard(region.launchTemplateId)}>
                        <FileCopyIcon style={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* ASG live status */}
            {asg ? (
              <Box mt={2}>
                <Typography variant="caption" color="textSecondary">ASG Live Status</Typography>
                <Box display="flex" gap={16} flexWrap="wrap" mt={0.5}>
                  <Typography variant="body2"><strong>{asg.runningInstances}</strong> running / <strong>{asg.desiredCapacity}</strong> desired</Typography>
                  <Typography variant="body2">Min: <strong>{asg.minSize}</strong> · Max: <strong>{asg.maxSize}</strong></Typography>
                  {region.mtdCostUsd != null && (
                    <Typography variant="body2" color="textSecondary">
                      MTD cost: <strong>${region.mtdCostUsd.toFixed(2)}</strong>
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : region.asgProvisionStatus === 'pending' ? (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
                ASG provisioning in progress for this region. Ouro is creating the target security group, launch template, and Auto Scaling group.
              </Typography>
            ) : region.asgName ? (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
                ASG: {region.asgName} (fetching live data…)
              </Typography>
            ) : region.asgProvisionStatus === 'failed' ? (
              <Box mt={1} p={1} bgcolor="action.hover" borderRadius={4}>
                <Typography variant="caption" color="error">
                  ASG provisioning failed for this region. Use "Retry Provision" above to re-attempt with the latest source AMI, or run the AWS permission scan to diagnose further.
                </Typography>
              </Box>
            ) : (
              <Box mt={1} p={1} bgcolor="action.hover" borderRadius={4}>
                <Typography variant="caption" color="textSecondary">
                  No managed ASG is registered for this region yet.
                </Typography>
              </Box>
            )}

            {/* Capacity controls */}
            <Box className={classes.capacityRow}>
              <TextField
                label="Min"
                type="number"
                size="small"
                variant="outlined"
                className={classes.capacityInput}
                value={capacityEdit.min}
                onChange={(e) => {
                  const value = e.target.value;
                  setCapacityEdit((c) => ({ ...c, min: value }));
                }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Max"
                type="number"
                size="small"
                variant="outlined"
                className={classes.capacityInput}
                value={capacityEdit.max}
                onChange={(e) => {
                  const value = e.target.value;
                  setCapacityEdit((c) => ({ ...c, max: value }));
                }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Desired"
                type="number"
                size="small"
                variant="outlined"
                className={classes.capacityInput}
                value={capacityEdit.desired}
                onChange={(e) => {
                  const value = e.target.value;
                  setCapacityEdit((c) => ({ ...c, desired: value }));
                }}
                inputProps={{ min: 0 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleSaveCapacity}
                disabled={savingCapacity || !region.asgName}
                startIcon={savingCapacity ? <CircularProgress size={14} /> : null}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={handleShutdown}
                disabled={savingCapacity || !region.asgName}
                startIcon={<PowerIcon fontSize="small" />}
              >
                Shutdown to 0
              </Button>
            </Box>

            {/* Capacity planner warm schedule */}
            <Box mt={2} p={1.5} bgcolor="action.hover" borderRadius={4}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gridGap={8}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Capacity Planner Warm Schedule
                  </Typography>
                  <Typography variant="body2">
                    {warmScheduleEdit.enabled
                      ? `Keeps at least ${warmScheduleEdit.warmMinimum || 1} server${Number(warmScheduleEdit.warmMinimum) === 1 ? '' : 's'} warm during ${warmWindowLabel}.`
                      : 'Disabled for this region. Scaling is demand-driven only.'}
                  </Typography>
                  {showAsgMinFloorHint && (
                    <Typography variant="caption" color="secondary">
                      ASG Min is still {asgMinFloor}; set Min to 0 or use Shutdown to 0 to allow full scale-down.
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary">
                    Local reference: {warmLocalLabel}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={warmScheduleEdit.enabled === true}
                      onChange={() => setWarmScheduleEdit((current) => ({ ...current, enabled: !current.enabled }))}
                      color="primary"
                      size="small"
                    />
                  }
                  label={warmScheduleEdit.enabled ? 'Warm floor enabled' : 'Warm floor disabled'}
                />
              </Box>

              <Grid container spacing={1} style={{ marginTop: 8 }} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Warm minimum"
                    type="number"
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={warmScheduleEdit.warmMinimum}
                    onChange={(e) => {
                      const value = Math.max(1, Number(e.target.value) || 1);
                      setWarmScheduleEdit((current) => ({ ...current, warmMinimum: value }));
                    }}
                    inputProps={{ min: 1, max: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Open UTC</InputLabel>
                    <Select
                      value={Number(warmScheduleEdit.openHourUTC)}
                      label="Open UTC"
                      onChange={(e) => setWarmScheduleEdit((current) => ({ ...current, openHourUTC: Number(e.target.value) }))}
                    >
                      {HOURS.map((hour) => (
                        <MenuItem key={hour} value={hour}>{padHour(hour)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Close UTC</InputLabel>
                    <Select
                      value={Number(warmScheduleEdit.closeHourUTC)}
                      label="Close UTC"
                      onChange={(e) => setWarmScheduleEdit((current) => ({ ...current, closeHourUTC: Number(e.target.value) }))}
                    >
                      {HOURS.map((hour) => (
                        <MenuItem key={hour} value={hour}>{padHour(hour)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box display="flex" gridGap={8} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleUseSuggestedWarmSchedule}
                    >
                      Use Suggested
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleSaveWarmSchedule}
                      disabled={savingWarmSchedule}
                      startIcon={savingWarmSchedule ? <CircularProgress size={14} /> : null}
                    >
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Box mt={1.5}>
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: 4, display: 'block' }}>
                  Idle suppression — turns off the warm server when no meaningful queue activity is detected.
                </Typography>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Idle timeout (min)"
                      type="number"
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={warmScheduleEdit.idleTimeoutMinutes}
                      onChange={(e) => {
                        const value = Math.max(5, Math.min(60, Number(e.target.value) || 15));
                        setWarmScheduleEdit((current) => ({ ...current, idleTimeoutMinutes: value }));
                      }}
                      inputProps={{ min: 5, max: 60 }}
                      helperText="Minutes of no activity before suppressing warm floor"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Re-warm min players"
                      type="number"
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={warmScheduleEdit.reactivationMinPlayers}
                      onChange={(e) => {
                        const value = Math.max(1, Math.min(20, Number(e.target.value) || 5));
                        setWarmScheduleEdit((current) => ({ ...current, reactivationMinPlayers: value }));
                      }}
                      inputProps={{ min: 1, max: 20 }}
                      helperText="Minimum players queueing to re-activate warm floor"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Re-warm duration (min)"
                      type="number"
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={warmScheduleEdit.reactivationMinMinutes}
                      onChange={(e) => {
                        const value = Math.max(1, Math.min(10, Number(e.target.value) || 1));
                        setWarmScheduleEdit((current) => ({ ...current, reactivationMinMinutes: value }));
                      }}
                      inputProps={{ min: 1, max: 10 }}
                      helperText="Minutes of sustained activity needed to re-warm"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Bake + Propagate actions */}
            <Box className={classes.actionRow}>
              {region.isSourceRegion && (
                <Button
                  variant="contained"
                  size="small"
                  color="default"
                  onClick={() => setBakeDialogOpen(true)}
                  disabled={isBaking || isPending || !region.asgName || hasBakeInProgress}
                  startIcon={isBaking ? <CircularProgress size={14} /> : <BuildIcon fontSize="small" />}
                >
                  {isBaking ? 'Baking…' : 'Bake New AMI'}
                </Button>
              )}
              {(region.amiBakeStatus === 'done' || region.isSourceRegion) && region.amiId && (
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={handlePropagate}
                  disabled={isPending}
                  startIcon={<PublishIcon fontSize="small" />}
                >
                  Propagate AMI to All Regions
                </Button>
              )}
            </Box>

            {isPending && (
              <Typography className={classes.progressText} style={{ marginTop: 8 }}>
                <HourglassIcon style={{ fontSize: 12, verticalAlign: 'middle' }} /> Background job in progress — auto-refreshes every 15s
              </Typography>
            )}
          </>
        )}
      </CardContent>

      <BakeAmiDialog
        open={bakeDialogOpen}
        onClose={() => setBakeDialogOpen(false)}
        onSubmit={handleBake}
        regionId={region._id}
        userId={userId}
      />

      <Snackbar
        open={!!snack}
        message={snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
      />
    </Card>
  );
}

// ─── Main RegionManagement component ──────────────────────────────────────────
const RegionManagement = ({ userId }) => {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const [regions, setRegions] = useState([]);
  const [machineTypeMigrationSummary, setMachineTypeMigrationSummary] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  const [orphanCleanupResult, setOrphanCleanupResult] = useState(null);
  const [snack, setSnack] = useState(null);
  const pollRef = useRef(null);

  const fetchRegions = useCallback(async () => {
    if (!userId) return;
    if (isMountedRef.current) setLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/adpass/${userId}`));
      const data = await res.json();
      if (isMountedRef.current && data.ok) {
        setRegions(data.regions || []);
        setMachineTypeMigrationSummary(data.machineTypeMigrationSummary || null);
      }
    } catch (err) {
      console.error('[RegionManagement] fetch error:', err.message);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [isMountedRef, userId]);

  const fetchCountries = useCallback(async () => {
    if (!userId) return;
    if (isMountedRef.current) setLoadingCountries(true);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-region-countries/adpass/${userId}`));
      const data = await res.json();
      if (isMountedRef.current && data.ok) {
        setCountryOptions(data.countries || []);
      }
    } catch (err) {
      console.error('[RegionManagement] countries fetch error:', err.message);
    } finally {
      if (isMountedRef.current) setLoadingCountries(false);
    }
  }, [isMountedRef, userId]);

  useEffect(() => {
    fetchRegions();
    fetchCountries();
    pollRef.current = setInterval(fetchRegions, 15_000);
    return () => clearInterval(pollRef.current);
  }, [fetchCountries, fetchRegions]);

  const handleAddRegion = async (form) => {
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/adpass/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || data.err);
      setSnack(
        form.cold
          ? `Cold region ${form.awsRegion} added — it is visible to players, with no infrastructure provisioned yet.`
          : `Region ${form.awsRegion} added — AMI, launch template, and ASG provisioning started in background.`
      );
      setAddDialogOpen(false);
      fetchRegions();
      return true;
    } catch (err) {
      setSnack(`Error: ${err.message}`);
      return false;
    }
  };

  const handlePurge = async () => {
    if (!window.confirm('Purge old unused AMIs and their backing snapshots across all enabled regions? The current active AMI and one rollback AMI per region will be kept.')) return;
    setPurging(true);
    setPurgeResult(null);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/purge-old-amis/adpass/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.err || 'Purge failed');
      setPurgeResult(data);
      setSnack(`Purge complete: ${data.totalDeletedAmis} AMI(s) deregistered, ${data.totalDeletedSnapshots} snapshot(s) deleted across ${data.regionsScanned} region(s)`);
      fetchRegions();
    } catch (err) {
      setSnack(`Purge error: ${err.message}`);
    } finally {
      setPurging(false);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm(
      'Scan all AWS regions and delete Ouro-managed infrastructure (ASG, launch template, AMIs, snapshots, security groups) for any region that is no longer in the database?\n\n' +
      'This targets regions you deleted before cleanup was automatic. It will NOT touch regions still present in the database (including disabled ones).'
    )) return;
    setCleaningOrphans(true);
    setOrphanCleanupResult(null);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/cleanup-orphaned-infra/adpass/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.err || 'Orphan cleanup failed');
      setOrphanCleanupResult(data);
      setSnack(data.message || 'Orphan cleanup complete');
    } catch (err) {
      setSnack(`Orphan cleanup error: ${err.message}`);
    } finally {
      setCleaningOrphans(false);
    }
  };

  const handleMigrateMachineType = async () => {
    if (!window.confirm(
      `Update launch templates and ASGs in all affected regions to use ${machineTypeMigrationSummary?.desiredInstanceType || 'c7a.large'}?\n\n` +
      'Currently running instances will NOT be terminated — only future instances will use the new type.'
    )) return;
    setMigrating(true);
    setMigrateResult(null);
    try {
      const res = await fetch(apiUrl(`/api/v7/admin/server-regions/migrate-machine-type/adpass/${userId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.err || 'Migration failed');
      setMigrateResult(data);
      const succeeded = (data.results || []).filter((r) => r.ok).length;
      const failed = (data.results || []).filter((r) => !r.ok).length;
      setSnack(
        failed > 0
          ? `Migration done: ${succeeded} succeeded, ${failed} failed. Check the panel for details.`
          : `Migration complete: ${succeeded} region(s) updated to ${data.desiredInstanceType}.`
      );
      fetchRegions();
    } catch (err) {
      setSnack(`Migration error: ${err.message}`);
    } finally {
      setMigrating(false);
    }
  };

  // Summary stats
  const totalRunning = regions.reduce((s, r) => s + (r.liveAsg?.runningInstances || 0), 0);
  const totalDesired = regions.reduce((s, r) => s + (r.liveAsg?.desiredCapacity || 0), 0);
  const totalMtdCost = regions.reduce((s, r) => s + (r.mtdCostUsd || 0), 0);
  const hasBakeInProgress = regions.some((r) => r.amiBakeStatus === 'baking');
  const hasPending = regions.some(
    (r) =>
      r.amiCopyStatus === 'pending' ||
      r.ltCopyStatus === 'pending' ||
      r.asgProvisionStatus === 'pending' ||
      r.amiBakeStatus === 'baking'
  );
  const frankfurtRegion = regions.find((r) => r.isSourceRegion);

  return (
    <Box className={classes.root}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" className={classes.sectionTitle} style={{ marginBottom: 0 }}>
          <CloudIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Infrastructure — Server Regions
        </Typography>
        <Box display="flex" gap={8}>
          {hasPending && (
            <Chip size="small" icon={<HourglassIcon />} label="Jobs in progress" color="default" />
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={fetchRegions} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Region
          </Button>
        </Box>
      </Box>

      {/* Cost + instance summary */}
      <Paper variant="outlined" style={{ padding: 16, marginBottom: 20 }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary">Total Running</Typography>
            <Typography variant="h5" color="primary">{totalRunning}</Typography>
            <Typography variant="caption" color="textSecondary">{totalDesired} desired</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary">Regions</Typography>
            <Typography variant="h5">{regions.length}</Typography>
            <Typography variant="caption" color="textSecondary">
              {regions.filter((r) => r.enabled && !r.isCold).length} active · {regions.filter((r) => r.isCold).length} cold
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary">MTD EC2 Cost</Typography>
            <Typography variant="h5" color={totalMtdCost > 0 ? 'textPrimary' : 'textSecondary'}>
              ${totalMtdCost.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="textSecondary">accumulated this month</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary">Active Jobs</Typography>
            <Typography variant="h5">{regions.filter((r) => r.amiCopyStatus === 'pending' || r.ltCopyStatus === 'pending' || r.asgProvisionStatus === 'pending' || r.amiBakeStatus === 'baking').length}</Typography>
            <Typography variant="caption" color="textSecondary">Provisioning jobs / bakes</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Machine Type Migration Notice */}
      {machineTypeMigrationSummary &&
        (machineTypeMigrationSummary.regionsNeedingMigration > 0 ||
          machineTypeMigrationSummary.ltReadErrors > 0) && (
        <Paper variant="outlined" style={{ padding: 16, marginBottom: 20, borderColor: '#ed6c02' }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" style={{ gap: 12 }}>
            <Box display="flex" alignItems="flex-start" style={{ gap: 8, flex: 1 }}>
              <WarningIcon style={{ color: '#ed6c02', marginTop: 2, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Machine Type Update Available
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                  {machineTypeMigrationSummary.regionsNeedingMigration} region{machineTypeMigrationSummary.regionsNeedingMigration > 1 ? 's' : ''} still use the old instance type.
                  {machineTypeMigrationSummary.ltReadErrors > 0 &&
                    ` ${machineTypeMigrationSummary.ltReadErrors} region${machineTypeMigrationSummary.ltReadErrors > 1 ? 's' : ''} could not be inspected.`}
                  {' '}
                  Migrate to <code style={{ fontSize: '0.85em' }}>{machineTypeMigrationSummary.desiredInstanceType}</code> to update launch templates and ASGs.
                  Currently running instances are not affected.
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 8 }}>
                  {(machineTypeMigrationSummary.affectedRegions || []).map((r) => (
                    <Chip
                      key={r.awsRegion}
                      size="small"
                      label={`${r.displayName} (${r.currentInstanceType || '?'})`}
                      variant="outlined"
                    />
                  ))}
                </Box>
                {machineTypeMigrationSummary.ltReadErrors > 0 && (
                  <Box mt={1}>
                    <Typography variant="caption" color="error" component="div" style={{ fontWeight: 600 }}>
                      Could not read launch template instance type:
                    </Typography>
                    {(machineTypeMigrationSummary.ltReadErrorRegions || []).map((r) => (
                      <Typography key={r.awsRegion} variant="caption" color="error" component="div">
                        {r.displayName} ({r.awsRegion}): {r.error}
                      </Typography>
                    ))}
                  </Box>
                )}
                {migrateResult && (
                  <Box mt={1.5}>
                    {(migrateResult.results || []).filter((r) => !r.ok).map((r) => (
                      <Typography key={r.awsRegion} variant="caption" color="error" component="div">
                        {r.displayName} ({r.awsRegion}): {r.error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
            <Button
              variant="contained"
              size="small"
              style={{ backgroundColor: '#ed6c02', color: '#fff', flexShrink: 0 }}
              onClick={handleMigrateMachineType}
              disabled={migrating}
              startIcon={migrating ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <SystemUpdateIcon />}
            >
              {migrating ? 'Migrating…' : 'Migrate Machine Type'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* AMI Bake Status (Frankfurt) */}
      {frankfurtRegion && (frankfurtRegion.amiBakeStatus === 'baking' || frankfurtRegion.amiBakeStatus === 'failed' || frankfurtRegion.amiBakeStatus === 'done') && (
        <Paper variant="outlined" style={{ padding: 16, marginBottom: 20 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
            <BuildIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 6 }} />
            AMI Bake Status — Frankfurt
          </Typography>

          {frankfurtRegion.amiBakeStatus === 'baking' && (
            <Box>
              <Box display="flex" alignItems="center" gap={8} mb={1}>
                <CircularProgress size={16} />
                <Typography variant="body2">Bake in progress</Typography>
              </Box>
              {frankfurtRegion.pendingBake && (
                <Box ml={3}>
                  <Typography variant="caption" color="textSecondary" component="div">
                    Instance: <span style={{ fontFamily: 'monospace' }}>{frankfurtRegion.pendingBake.instanceId}</span>
                  </Typography>
                  <Typography variant="caption" color="textSecondary" component="div">
                    Image name: <span style={{ fontFamily: 'monospace' }}>{frankfurtRegion.pendingBake.imageName}</span>
                  </Typography>
                  {frankfurtRegion.bakeStartedAt && (
                    <Typography variant="caption" color="textSecondary" component="div">
                      Started: {new Date(frankfurtRegion.bakeStartedAt).toLocaleString()}
                    </Typography>
                  )}
                  {frankfurtRegion.pendingBakeAmiId && (
                    <Typography variant="caption" color="textSecondary" component="div">
                      AMI ID: <span style={{ fontFamily: 'monospace' }}>{frankfurtRegion.pendingBakeAmiId}</span>
                    </Typography>
                  )}
                </Box>
              )}
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                <HourglassIcon style={{ fontSize: 12, verticalAlign: 'middle' }} /> Auto-refreshes every 15s. This typically takes 10–30 minutes.
              </Typography>
            </Box>
          )}

          {frankfurtRegion.amiBakeStatus === 'failed' && (
            <Box p={1} bgcolor="action.hover" borderRadius={4}>
              <Box display="flex" alignItems="center" gap={8} mb={0.5}>
                <ErrorIcon color="secondary" style={{ fontSize: 18 }} />
                <Typography variant="body2" color="secondary">Last bake failed</Typography>
              </Box>
              {frankfurtRegion.bakeFailureReason && (
                <Typography variant="caption" color="textSecondary" component="div" style={{ fontFamily: 'monospace', marginLeft: 26 }}>
                  {frankfurtRegion.bakeFailureReason}
                </Typography>
              )}
              {frankfurtRegion.bakeFailureAt && (
                <Typography variant="caption" color="textSecondary" component="div" style={{ marginLeft: 26, marginTop: 4 }}>
                  Failed at: {new Date(frankfurtRegion.bakeFailureAt).toLocaleString()}
                </Typography>
              )}
            </Box>
          )}

          {frankfurtRegion.amiBakeStatus === 'done' && (
            <Box display="flex" alignItems="center" gap={8}>
              <CheckCircleIcon color="primary" style={{ fontSize: 18 }} />
              <Typography variant="body2">
                Last bake completed
                {frankfurtRegion.lastBakedAt ? ` at ${new Date(frankfurtRegion.lastBakedAt).toLocaleString()}` : ''}
              </Typography>
              {frankfurtRegion.amiId && (
                <Chip size="small" variant="outlined" label={frankfurtRegion.amiId} style={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* AMI Cleanup */}
      <Box display="flex" flexWrap="wrap" gap={8} mb={2}>
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          onClick={handlePurge}
          disabled={purging || hasBakeInProgress || hasPending}
          startIcon={purging ? <CircularProgress size={14} /> : <DeleteSweepIcon fontSize="small" />}
        >
          {purging ? 'Purging…' : 'Purge Old AMIs'}
        </Button>
        {purgeResult && purgeResult.totalDeletedAmis === 0 && purgeResult.totalErrors === 0 && (
          <Typography variant="caption" color="textSecondary" style={{ alignSelf: 'center' }}>
            No unused AMIs to clean up
          </Typography>
        )}
        <Tooltip title="Delete AWS infra (ASG, LT, AMIs, snapshots, SGs) for regions previously removed from the database">
          <span>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={handleCleanupOrphans}
              disabled={cleaningOrphans || hasPending}
              startIcon={cleaningOrphans ? <CircularProgress size={14} /> : <DeleteSweepIcon fontSize="small" />}
            >
              {cleaningOrphans ? 'Scanning…' : 'Cleanup Deleted Regions'}
            </Button>
          </span>
        </Tooltip>
        {orphanCleanupResult && orphanCleanupResult.scannedRegions === 0 && (
          <Typography variant="caption" color="textSecondary" style={{ alignSelf: 'center' }}>
            No orphaned regions found
          </Typography>
        )}
        {orphanCleanupResult && orphanCleanupResult.results && orphanCleanupResult.results.length > 0 && (
          <Box mt={1} width="100%">
            {orphanCleanupResult.results.map((r) => (
              <Typography key={r.awsRegion} variant="caption" color={r.ok ? 'textSecondary' : 'error'} component="div">
                {r.awsRegion}: {r.ok ? buildCleanupSummary(r.cleanup) : r.error}
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      <PermissionsDiagnosticPanel userId={userId} />

      {/* Region cards */}
      {regions.length === 0 && !loading && (
        <Typography color="textSecondary" align="center" style={{ marginTop: 32 }}>
          No regions configured yet. Click "Add Region" to get started.
        </Typography>
      )}
      {regions.map((region) => (
        <RegionCard
          key={region._id}
          region={region}
          userId={userId}
          onRefresh={fetchRegions}
          countryOptions={countryOptions}
          loadingCountries={loadingCountries}
          hasBakeInProgress={hasBakeInProgress}
        />
      ))}

      <AddRegionDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleAddRegion}
        userId={userId}
        countryOptions={countryOptions}
        loadingCountries={loadingCountries}
      />

      <Snackbar
        open={!!snack}
        message={snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
      />
    </Box>
  );
};

export default RegionManagement;
